import uniqBy from 'lodash/uniqBy';
import { Group } from 'react-konva';
import { prop } from 'rambda';
import { Rect } from 'components/Canvas/Rect';
import { Label, LabelOrientationEnum, RegionLabelProps } from 'components/Canvas/RegionLabel';
import { EditablePolygon, Polygon } from 'components/Canvas/Polygon';
import { Mask } from 'components/Canvas/Mask';
import { Point } from 'components/Canvas/Point';
import { useCallback, useMemo } from 'react';
import { UiRegionItem } from 'utils/cfUiItems/base';
import { isBboxShapeProps, isPolygonShapeProps } from 'utils/typeguards/predictionOutputs';
import { KonvaEventObject } from 'konva/types/Node';
import { observer } from 'mobx-react-lite';
import { CANVAS_INTERACTION_STATE } from 'utils/konva/types';
import { EditableBbox } from 'modules/Canvas/components/CanvasRegionList/EditableBbox';
import { calculatePolygonArea, uiItemToShapeConfig } from 'modules/Canvas/components/CanvasRegionList/helpers';
import { CanvasRegionAnnotation, RectConfig, PolygonConfig, MaskConfig } from 'modules/Canvas/helpers/types';
import { anno_isUiRegionItem } from 'utils/cfUiItems/guards';
import { isPointCanvasConfig, isPositivePointCanvasConfig, PointCanvasConfig } from 'utils/annotations/point';
import { Box } from 'components/Canvas/Box';

export type NullableShapeConfig = RectConfig | PolygonConfig | MaskConfig | PointCanvasConfig | null;
type KonvaMouseEvent = ((evt: KonvaEventObject<MouseEvent>) => void) | undefined;
export type MakeCanvasItemProps = (
  item: NullableShapeConfig,
  uiRegionItem?: UiRegionItem,
) => {
  onMouseEnter?: KonvaMouseEvent;
  onMouseLeave?: KonvaMouseEvent;
  onAccept?: (item: UiRegionItem) => void;
  opacity?: number;
  orientation?: LabelOrientationEnum;
  fillEnabled?: boolean;
};

export interface RegionMap {
  [regionId: string]: Label[];
}

export type RegionList = CanvasRegionAnnotation[] | UiRegionItem[];

type RegionListProps = {
  media: HTMLImageElement | HTMLVideoElement;
  list: {
    items: RegionList;
  };
  regionMap?: RegionMap;
  scale: number;
  makeItemProps?: MakeCanvasItemProps;
  shouldValidate?: boolean;
  selectedShape?: string | null;
  onBoundingBoxMouseDown?: (uiItem: CanvasRegionAnnotation) => void;
  onBoundingBoxEdit?: (uiItem: CanvasRegionAnnotation, updatedBoundingBox: CF.API.BoundingBox) => Promise<string | undefined | void>;
  onAcceptButtonClick?: (uiItem: CanvasRegionAnnotation, label: Label) => void;
  onCreateRegionAnnotationFromPrediction?: (uiItem: CanvasRegionAnnotation) => void;
  zoomScale?: number;
  interactionState?: CANVAS_INTERACTION_STATE;
  isBboxesVisible?: boolean;
  isPolygonsVisible?: boolean;
  onPatchPolygon?: (item: CanvasRegionAnnotation, points?: CF.API.Polygon['points']) => void;
  hiddenConceptKeys?: string[];
};

export function _CanvasRegionList({
  media,
  list,
  regionMap,
  scale = 1,
  makeItemProps,
  shouldValidate = false,
  selectedShape,
  onBoundingBoxEdit,
  onBoundingBoxMouseDown,
  onAcceptButtonClick,
  onCreateRegionAnnotationFromPrediction,
  zoomScale,
  interactionState,
  isBboxesVisible = false,
  isPolygonsVisible = false,
  onPatchPolygon,
  hiddenConceptKeys = [],
}: RegionListProps): JSX.Element | null {
  if (!media || !list) {
    return null;
  }

  const uniqRegions = useMemo(() => {
    // Sort regions by area
    const regions = sortRegionsByArea(list.items);

    // Avoid ID collisions by ensuring unique region keys
    const uniqueRegions = uniqBy(regions, prop('key'));

    // selected Shape moving to the top layer which will help user edit the selected shape easily
    if (selectedShape) {
      const selectedIndex = uniqueRegions.findIndex((region) => region.key === selectedShape);
      if (selectedIndex !== -1) {
        const selectedItem = uniqueRegions[selectedIndex];
        const newUniqueRegions = uniqueRegions.filter((_, index) => index !== selectedIndex).concat(selectedItem);
        return newUniqueRegions;
      }
    }

    return uniqueRegions;
  }, [list.items, selectedShape]);

  return (
    <Group>
      {uniqRegions.map((item) => {
        if (shouldValidate) {
          const hidden = (isBboxShapeProps(item.regionData) && !isBboxesVisible) || (isPolygonShapeProps(item.regionData) && !isPolygonsVisible);
          if (hidden) return null;
        }
        if (hiddenConceptKeys.includes(item.key)) return null;

        return (
          <Item
            key={item.key}
            item={item}
            regionMap={regionMap}
            media={media}
            scale={scale}
            makeItemProps={makeItemProps}
            selectedShape={selectedShape}
            onBoundingBoxEdit={onBoundingBoxEdit}
            onBoundingBoxMouseDown={onBoundingBoxMouseDown}
            onAcceptButtonClick={onAcceptButtonClick}
            onCreateRegionAnnotationFromPrediction={onCreateRegionAnnotationFromPrediction}
            zoomScale={zoomScale}
            interactionState={interactionState}
            onPatchPolygon={onPatchPolygon}
          />
        );
      })}
    </Group>
  );
}

function calculateArea(region: CanvasRegionAnnotation | UiRegionItem): number {
  if (isBboxShapeProps(region.regionData)) {
    const { bbox } = region.regionData;
    return (bbox.bottom_row - bbox.top_row) * (bbox.right_col - bbox.left_col);
  } else if (isPolygonShapeProps(region.regionData)) {
    return calculatePolygonArea((region.regionData as CF.API.PolygonShapeProps).polygon);
  }
  return 0;
}

function sortRegionsByArea(regions: RegionList): RegionList {
  return regions.sort((a, b) => {
    const areaDiff = calculateArea(b) - calculateArea(a);

    if (areaDiff !== 0) {
      return areaDiff;
    }

    const isSuggestionA = (anno_isUiRegionItem(a) && a.annotation?.isSuggestion) || false;
    const isSuggestionB = (anno_isUiRegionItem(b) && b.annotation?.isSuggestion) || false;

    return Number(isSuggestionA) - Number(isSuggestionB);
  });
}

const Item = observer(
  ({
    media,
    item: uiItem,
    scale,
    makeItemProps,
    selectedShape,
    onBoundingBoxEdit,
    onBoundingBoxMouseDown,
    onAcceptButtonClick,
    onCreateRegionAnnotationFromPrediction,
    regionMap,
    zoomScale,
    interactionState,
    onPatchPolygon,
  }: Omit<RegionListProps, 'list'> & { item: RegionListProps['list']['items'][0] }) => {
    const shapeConfig = useMemo(() => (uiItem && media ? uiItemToShapeConfig(media)(uiItem) : null), [media, uiItem]);

    if (!shapeConfig || ('annotation' in uiItem && (uiItem as CanvasRegionAnnotation).annotation?.isHidden)) return null;
    const props: Partial<RegionLabelProps> = makeItemProps ? makeItemProps(shapeConfig, uiItem) : {};
    const handleBoundingBoxMouseDown = useCallback(
      (item: CanvasRegionAnnotation) => {
        onBoundingBoxMouseDown?.(item);
      },
      [onBoundingBoxMouseDown],
    );

    if ('points' in shapeConfig) {
      if ('annotation' in uiItem) {
        return (
          <EditablePolygon
            scale={scale}
            {...shapeConfig}
            item={uiItem as CanvasRegionAnnotation}
            opacity={props.opacity}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            zoomScale={zoomScale}
            onPatchPolygon={onPatchPolygon}
            fillPatternImage={props.fillPatternImage}
            {...props}
          />
        );
      }
      return <Polygon scale={scale} {...shapeConfig} zoomScale={zoomScale} opacity={props.opacity} />;
    }

    if ('base64' in shapeConfig) {
      const { base64, key, color, text } = shapeConfig;
      const uiRegionItem: UiRegionItem = {
        key,
        text: {
          raw: text || '',
        },
        regionData: {
          mask: {
            image: {
              base64,
            },
          },
          color,
        },
      };
      return <Mask {...shapeConfig} region={uiRegionItem} {...props} />;
    }

    if (isPointCanvasConfig(shapeConfig)) {
      if (isPositivePointCanvasConfig(shapeConfig)) {
        return (
          <Box
            key={`${shapeConfig.x}-${shapeConfig.y}`}
            x={shapeConfig.x}
            y={shapeConfig.y}
            color={shapeConfig.color}
            zoomScale={shapeConfig.zoomScale}
          />
        );
      }

      return (
        <Point
          scale={scale}
          {...shapeConfig}
          key={shapeConfig.key}
          opacity={props.opacity}
          onMouseEnter={props.onMouseEnter}
          onMouseLeave={props.onMouseLeave}
          zoomScale={zoomScale}
          radius={3}
          {...props}
        />
      );
    }

    const isPredicted = 'annotation' in uiItem && (uiItem as CanvasRegionAnnotation).annotation?.isSuggestion;
    return 'annotation' in uiItem && uiItem.key === selectedShape ? (
      <EditableBbox
        {...props}
        shouldOverdrawWholeArea
        shapeConfig={shapeConfig}
        item={uiItem as CanvasRegionAnnotation}
        scale={scale}
        media={media}
        opacity={props.opacity}
        onChange={onBoundingBoxEdit}
      />
    ) : (
      <Rect
        {...shapeConfig}
        {...props}
        labelProps={{
          onMouseEnter: props.onMouseEnter,
          onMouseLeave: props.onMouseLeave,
          onAcceptButtonClick: (label: Label) => onAcceptButtonClick?.(uiItem, label),
          scale: 0,
          orientation: props.orientation,
        }}
        labels={regionMap ? regionMap[uiItem.key] : []}
        scale={scale}
        id={uiItem.key}
        color={shapeConfig?.color || '#fff'}
        opacity={props.opacity}
        isShapeSelected={!!selectedShape}
        onClick={() => {
          if (isPredicted) {
            onCreateRegionAnnotationFromPrediction?.(uiItem);
          } else {
            handleBoundingBoxMouseDown(uiItem as CanvasRegionAnnotation);
          }
        }}
        isPredicted={isPredicted}
        zoomScale={zoomScale}
        interactionState={interactionState}
      />
    );
  },
);

const CanvasRegionList = observer(_CanvasRegionList);
export { CanvasRegionList };
